import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isEqual, isNil} from 'lodash-es';

export type FloatingPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface FloatingRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface FloatingPositionOptions {
  placement?: FloatingPlacement;
  fallbackPlacements?: FloatingPlacement[];
  offset?: number;
  padding?: number;
}

export interface FloatingPositionResult {
  styles: {
    floating: React.CSSProperties;
    arrow: React.CSSProperties;
  };
  placement: FloatingPlacement;
  isHidden: boolean;
}

const ARROW_SIZE = 10;
const DEFAULT_OFFSET = 6;
const DEFAULT_PADDING = 4;

const INITIAL_RESULT_STYLES: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  visibility: 'hidden'
};

// Determines whether there is enough room in the viewport to render the
// floating element at the given placement, without it being clipped.
export function hasSpaceFor(
  placement: FloatingPlacement,
  referenceRect: FloatingRect,
  floatingRect: FloatingRect,
  offset: number
): boolean {
  switch (placement) {
    case 'top':
      return referenceRect.top - offset - floatingRect.height >= 0;
    case 'bottom':
      return referenceRect.bottom + offset + floatingRect.height <= window.innerHeight;
    case 'left':
      return referenceRect.left - offset - floatingRect.width >= 0;
    case 'right':
      return referenceRect.right + offset + floatingRect.width <= window.innerWidth;
    default:
      return true;
  }
}

// Picks the preferred placement if there is room for it, otherwise falls
// back through the supplied fallback placements in order. If none fit,
// the preferred placement is used anyway (best effort).
export function resolvePlacement(
  preferredPlacement: FloatingPlacement,
  fallbackPlacements: FloatingPlacement[],
  referenceRect: FloatingRect,
  floatingRect: FloatingRect,
  offset: number
): FloatingPlacement {
  const candidates = [preferredPlacement, ...fallbackPlacements];

  for (const candidate of candidates) {
    if (hasSpaceFor(candidate, referenceRect, floatingRect, offset)) {
      return candidate;
    }
  }

  return preferredPlacement;
}

// Computes the unclamped viewport coordinates for the floating element,
// centered against the reference element along the cross axis.
export function computeCoordinates(
  referenceRect: FloatingRect,
  floatingRect: FloatingRect,
  placement: FloatingPlacement,
  offset: number
): {x: number; y: number} {
  switch (placement) {
    default:
    case 'top':
      return {
        x: referenceRect.left + referenceRect.width / 2 - floatingRect.width / 2,
        y: referenceRect.top - floatingRect.height - offset
      };
    case 'bottom':
      return {
        x: referenceRect.left + referenceRect.width / 2 - floatingRect.width / 2,
        y: referenceRect.bottom + offset
      };
    case 'left':
      return {
        x: referenceRect.left - floatingRect.width - offset,
        y: referenceRect.top + referenceRect.height / 2 - floatingRect.height / 2
      };
    case 'right':
      return {
        x: referenceRect.right + offset,
        y: referenceRect.top + referenceRect.height / 2 - floatingRect.height / 2
      };
  }
}

export function clamp(value: number, min: number, max: number): number {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

// Shifts the floating element along its cross axis so that it stays fully
// within the viewport, mirroring Popper's "preventOverflow" behaviour.
export function clampToViewport(
  x: number,
  y: number,
  floatingRect: FloatingRect,
  placement: FloatingPlacement,
  padding: number
): {x: number; y: number} {
  if (placement === 'top' || placement === 'bottom') {
    return {
      x: clamp(x, padding, window.innerWidth - floatingRect.width - padding),
      y
    };
  }

  return {
    x,
    y: clamp(y, padding, window.innerHeight - floatingRect.height - padding)
  };
}

// Computes the arrow's offset (in px, relative to the floating element's
// own edge) so that it points at the centre of the reference element,
// clamped so it never overshoots the floating element's bounds.
export function computeArrowOffset(
  referenceRect: FloatingRect,
  floatingPosition: {x: number; y: number},
  floatingRect: FloatingRect,
  placement: FloatingPlacement,
  padding: number
): number {
  if (placement === 'top' || placement === 'bottom') {
    const refCenter = referenceRect.left + referenceRect.width / 2;
    const relCenter = refCenter - floatingPosition.x - ARROW_SIZE / 2;
    return clamp(relCenter, padding, floatingRect.width - ARROW_SIZE - padding);
  }

  const referenceCenter = referenceRect.top + referenceRect.height / 2;
  const relativeCenter = referenceCenter - floatingPosition.y - ARROW_SIZE / 2;
  return clamp(relativeCenter, padding, floatingRect.height - ARROW_SIZE - padding);
}

export function arrowStyle(placement: FloatingPlacement, offset: number): React.CSSProperties {
  if (placement === 'top' || placement === 'bottom') {
    return {position: 'absolute', left: `${offset}px`};
  }

  return {position: 'absolute', top: `${offset}px`};
}

// Walks up the DOM tree from (but excluding) the given element, returning
// every ancestor that can scroll its contents (up to, but excluding, body).
export function getScrollParents(element: Element): Element[] {
  const parents: Element[] = [];
  let current = element.parentElement;

  while (!isNil(current) && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (/(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX)) {
      parents.push(current);
    }

    current = current.parentElement;
  }

  return parents;
}

// True when the reference element is not visible - either scrolled fully
// out of the viewport, or clipped by a scrollable/overflow-hidden ancestor.
export function isReferenceClipped(referenceElement: Element, referenceRect: FloatingRect): boolean {
  if (referenceRect.width === 0 && referenceRect.height === 0) {
    return true;
  }

  if (
    referenceRect.bottom <= 0 || referenceRect.top >= window.innerHeight ||
    referenceRect.right <= 0 || referenceRect.left >= window.innerWidth
  ) {
    return true;
  }

  let current = referenceElement.parentElement;
  while (!isNil(current) && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (/(auto|scroll|hidden)/.test(style.overflow + style.overflowY + style.overflowX)) {
      const parentRect = current.getBoundingClientRect();
      if (
        referenceRect.bottom <= parentRect.top || referenceRect.top >= parentRect.bottom ||
        referenceRect.right <= parentRect.left || referenceRect.left >= parentRect.right
      ) {
        return true;
      }
    }

    current = current.parentElement;
  }

  return false;
}

/**
 * Positions a floating element (e.g. a tooltip or popover) relative to a
 * reference element, using `position: fixed` viewport coordinates.
 *
 * Supports flipping to a fallback placement when there isn't room, keeping
 * the floating element within the viewport, positioning an optional arrow
 * to point at the reference element, and hiding the floating element when
 * the reference is scrolled out of view (including inside scroll
 * containers).
 *
 * This is a lightweight internal replacement for react-popper / @popperjs.
 */
export function useFloatingPosition(
  referenceElement: Element,
  floatingElement: HTMLElement,
  arrowElement: HTMLElement,
  options: FloatingPositionOptions = {}
): FloatingPositionResult {
  const {
    placement: preferredPlacement = 'top',
    fallbackPlacements = [],
    offset = DEFAULT_OFFSET,
    padding = DEFAULT_PADDING
  } = options;

  const [result, setResult] = useState<FloatingPositionResult>({
    styles: {floating: INITIAL_RESULT_STYLES, arrow: {}},
    placement: preferredPlacement,
    isHidden: false
  });

  const frameHandle = useRef<number>(null);

  // Callers commonly pass the fallbacks as an inline array literal. Keyed on its contents, so a fresh array each render
  // doesn't rebuild recalculate, re-run the effect and set a new result - an endless render loop.
  const fallbackPlacementsKey = fallbackPlacements.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFallbackPlacements = useMemo(() => fallbackPlacements, [fallbackPlacementsKey]);

  const recalculate = useCallback(
    () => {
      if (isNil(referenceElement) || isNil(floatingElement)) {
        return;
      }

      const referenceRect = referenceElement.getBoundingClientRect();
      const floatingRect = floatingElement.getBoundingClientRect();

      const placement = resolvePlacement(preferredPlacement, stableFallbackPlacements, referenceRect, floatingRect, offset);
      const coordinates = computeCoordinates(referenceRect, floatingRect, placement, offset);
      const clamped = clampToViewport(coordinates.x, coordinates.y, floatingRect, placement, padding);
      const arrowOffset = computeArrowOffset(referenceRect, clamped, floatingRect, placement, padding);
      const isHidden = isReferenceClipped(referenceElement, referenceRect);

      const next: FloatingPositionResult = {
        styles: {
          floating: {
            position: 'fixed',
            top: `${Math.round(clamped.y)}px`,
            left: `${Math.round(clamped.x)}px`
          },
          arrow: isNil(arrowElement) ? {} : arrowStyle(placement, arrowOffset)
        },
        placement,
        isHidden
      };
      // Keep the previous result when nothing moved, so a recalculation never re-renders the caller for nothing
      setResult(previous => isEqual(previous, next) ? previous : next);
    },
    [referenceElement, floatingElement, arrowElement, preferredPlacement, stableFallbackPlacements, offset, padding]
  );

  const scheduleRecalculate = useCallback(
    () => {
      if (!isNil(frameHandle.current)) {
        return;
      }

      frameHandle.current = window.requestAnimationFrame(() => {
        frameHandle.current = null;
        recalculate();
      });
    },
    [recalculate]
  );

  useEffect(
    () => {
      if (isNil(referenceElement) || isNil(floatingElement)) {
        return undefined;
      }

      recalculate();

      const scrollParents = getScrollParents(referenceElement);
      const scrollTargets: (Window | Element)[] = [window, ...scrollParents];
      scrollTargets.forEach(target => target.addEventListener('scroll', scheduleRecalculate, {passive: true}));
      window.addEventListener('resize', scheduleRecalculate, {passive: true});

      const resizeObserver = new ResizeObserver(scheduleRecalculate);
      resizeObserver.observe(referenceElement);
      resizeObserver.observe(floatingElement);

      return () => {
        scrollTargets.forEach(target => target.removeEventListener('scroll', scheduleRecalculate));
        window.removeEventListener('resize', scheduleRecalculate);
        resizeObserver.disconnect();

        if (!isNil(frameHandle.current)) {
          window.cancelAnimationFrame(frameHandle.current);
          frameHandle.current = null;
        }
      };
    },
    [referenceElement, floatingElement, recalculate, scheduleRecalculate]
  );

  return result;
}
